import {
  Circle,
  GoogleMap,
  InfoWindow,
  LoadScript,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { useCallback, useEffect, useState } from "react";
import { FacilityProps } from "../../../helpers/types/getTypes";
import { InfoWindowFacility } from "../../commons/card/Card";

import { MinusIcon, PlusIcon } from "../../commons/icon/SvgIcon";
import Image from "../../commons/image/Image";
import { LoadingMap } from "../../commons/loading/Skeleton";

import { LOADER, MAP_DEFAULT, OPTIONS } from "./helper";

const MapRegion = ({
  mode,
  onChange,
  data,
}: {
  mode: string;
  onChange?: (e: any, v: any) => void;
  data: FacilityProps[];
}) => {
  const { isLoaded, loadError } = useJsApiLoader(LOADER as any);

  const [defaultCenter, setDefaultCenter] = useState(
    data.length > 0
      ? {
          lat: data[0].lat,
          lng: data[0].lng,
        }
      : MAP_DEFAULT
  );

  const [zoom, setZoom] = useState<{ default: number; min: number }>({
    default: 11,
    min: 4,
  });

  const [selectedElement, setSelectedElement] = useState<any>(null);
  const [map, setMap] = useState<any>(null);
  const onLoad = useCallback(
    function callback(map: any) {
      setMap(map);
    },
    [defaultCenter]
  );

  useEffect(() => {
    if (map) {
      const bounds = new window.google.maps.LatLngBounds(defaultCenter);
      data.forEach((e: any) => {
        bounds.extend({
          lat: e.lat,
          lng: e.lng,
        });
      });
      map.fitBounds(bounds);
    }
  }, [map]);

  return (
    <>
      {isLoaded ? (
        <GoogleMap
          id="map_sual"
          mapContainerClassName="w-full h-full rounded-[0.875rem] flex flex-col p-4.5 focus:!outline-none focus-visible:!outline-none"
          center={defaultCenter}
          zoom={zoom.default}
          options={{
            ...OPTIONS,
            gestureHandling: "none",
          }}
          onLoad={onLoad}
        >
          {/* Child components, such as markers, info windows, etc. */}
          <>
            {data.map((e, key: number) => (
              <Marker
                key={key}
                icon="/images/map/icon/pin-marker.svg"
                position={{
                  lat: e.lat,
                  lng: e.lng,
                }}
                options={{
                  map,
                }}
                onClick={() => {
                  setSelectedElement(e);
                }}
                onMouseOver={() => {
                  setSelectedElement(e);
                }}
                onMouseOut={() => {
                  setSelectedElement(null);
                }}
              />
            ))}

            {selectedElement ? (
              <InfoWindow
                position={{
                  lat: selectedElement.lat,
                  lng: selectedElement.lng,
                }}
                onCloseClick={() => {
                  setSelectedElement(null);
                }}
                options={{
                  disableAutoPan: true,
                }}
              >
                <InfoWindowFacility data={selectedElement} />
              </InfoWindow>
            ) : null}

            <div className="relative mt-auto">
              {/* MAP CONFIG */}
              <div className="absolute right-2 bottom-0 flex flex-col space-y-2">
                <div className="w-auto shadow-lv3 flex flex-col bg-transparent rounded-xl">
                  <div
                    className="p-3 pb-1.5 bg-white rounded-t-xl hover:bg-neutral-200 cursor-pointer transition ease-in-out duration-200"
                    onClick={() => {
                      const res = (map?.getZoom() || 0) + 1;
                      setZoom({ ...zoom, default: res });
                    }}
                  >
                    <PlusIcon />
                  </div>
                  <div
                    className="p-3 pt-1.5 bg-white rounded-b-xl hover:bg-neutral-200 cursor-pointer transition ease-in-out duration-200"
                    onClick={() => {
                      const res = (map?.getZoom() || 0) - 1;
                      setZoom({ ...zoom, default: res });
                    }}
                  >
                    <MinusIcon />
                  </div>
                </div>
              </div>
            </div>
          </>
        </GoogleMap>
      ) : (
        <LoadingMap />
      )}

      {loadError && (
        <div className="p-6 text-neutral-900">
          Map cannot be loaded right now, sorry.
        </div>
      )}
    </>
  );
};

export default MapRegion;
